import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ChoiceLayout, CustomFieldType, TextFormat} from '@ee/common/enums';
import {AbstractControl, FormControl, UntypedFormGroup} from '@angular/forms';
import {CustomFieldOption, resolveMultiSelectOptions} from '@ee/common/models';
import {Subscription} from 'rxjs';

@Component({
  selector: 'ee-custom-field',
  template: `
    <div class="w-full flex flex-row justify-center items-center" [formGroup]="form">
      @switch (form.get('type').value) {
        @case (fieldTypes.DATE) {
          <mat-form-field class="flex-1" appearance="outline"
                          [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <input matInput [formControl]="valueControl" [matDatepicker]="picker"
                   [required]="!builder && form.get('required').value">
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            @if (form.get('value')?.hasError('matDatepickerParse')) {
              <mat-error>Enter a date as MM/DD/YYYY.</mat-error>
            } @else if (!builder && form.get('value')?.hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
        @case (fieldTypes.DATETIME) {
          <mat-form-field class="flex-1" appearance="outline"
                          [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <input matInput eeDatetimeInput [formControl]="valueControl"
                   [required]="!builder && form.get('required').value">
            @if (!builder && form.get('value')?.hasError('required')) {
              <mat-error>Please enter a value.</mat-error>
            }
            <mat-hint>
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
        @case (fieldTypes.DECIMAL) {
          <mat-form-field class="flex-1" appearance="outline" [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            @if (isMonetary) {
              <span matPrefix>$ &nbsp;</span>
            }
            <input matInput type="number" [formControl]="valueControl"
                   [required]="!builder && form.get('required').value">
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
        @case (fieldTypes.SELECT) {
          <mat-form-field class="flex-1" appearance="outline"
                          [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <mat-select [formControl]="valueControl" [required]="!builder && form.get('required').value">
              @for (option of form.get('options').value; track option) {
                <mat-option [value]="option">
                  {{option}}
                </mat-option>
              }
            </mat-select>
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
        @case (fieldTypes.BOOLEAN) {
          <div class="flex-1">
            @if (form.get('true_label').value && form.get('false_label').value) {
              <mat-radio-group class="ee" [formControl]="valueControl">
                <mat-radio-button class="boolean-selection mr-4" [value]="form.get('true_label').value">
                  <b>{{form.get('true_label').value}}</b>
                </mat-radio-button>
                <mat-radio-button class="boolean-selection" [value]="form.get('false_label').value">
                  <b>{{form.get('false_label').value}}</b>
                </mat-radio-button>
              </mat-radio-group>
            } @else {
              <mat-slide-toggle [formControl]="valueControl" class="compact mb-4">
                {{form.get('label').value}}
              </mat-slide-toggle>
            }
          </div>
        }
        @case (fieldTypes.MULTI_SELECT) {
          <!-- Mirrors ee-field's checkbox group: same fieldset/legend grouping, same union
               rendering, same [checked]/(change) binding. The two renderers must agree, because a
               field collected on a step is read back on the intake form and vice versa. -->
          <fieldset class="flex-1 multi-select"
                    [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact, 'is-horizontal': horizontal}"
                    [attr.aria-invalid]="showMultiSelectRequiredError"
                    [attr.aria-describedby]="showMultiSelectRequiredError ? multiSelectErrorId : null">
            <legend class="multi-select__label">
              {{form.get('label').value}}@if (!builder && form.get('required').value) {<span class="multi-select__required" aria-hidden="true"> *</span>}
            </legend>
            @for (option of multiSelectOptions; track option.value) {
              <mat-checkbox class="multi-select__option" [checked]="isMultiSelectOptionSelected(option.value)"
                            [disabled]="multiSelectControl?.disabled"
                            (change)="toggleMultiSelectOption(option.value, $event.checked)">
                {{option.label}}
              </mat-checkbox>
            }
            @if (showMultiSelectRequiredError) {
              <div class="error multi-select__error" [id]="multiSelectErrorId">Select at least one option.</div>
            }
            <div class="multi-select__hint">
              <ng-container [ngTemplateOutlet]="hint"></ng-container>
            </div>
          </fieldset>
        }
        @case (fieldTypes.RADIO) {
          <div class="flex-1">
            <div class="choice-group__label">
              {{form.get('label').value}}@if (!builder && form.get('required').value) {<span aria-hidden="true"> *</span>}
            </div>
            <mat-radio-group class="choice-group" [class.is-horizontal]="horizontal" [formControl]="valueControl">
              @for (option of choiceOptions; track option.value) {
                <mat-radio-button [value]="option.value">{{option.label}}</mat-radio-button>
              }
              <!-- Same reason as the intake renderer: a radio cannot be un-picked, so an optional
                   field needs an explicit way back to blank. Required fields get none. -->
              @if (!form.get('required').value) {
                <mat-radio-button [value]="null">
                  <span class="choice-group__none">— No selection —</span>
                </mat-radio-button>
              }
            </mat-radio-group>
          </div>
        }
        @case (fieldTypes.TIME) {
          <mat-form-field class="flex-1" appearance="outline" [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <input matInput [formControl]="valueControl"
                   [required]="!builder && form.get('required').value" [ngxMatTimepicker]="matTimepicker">
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
            <ngx-mat-timepicker #matTimepicker [defaultTime]="'3:00 pm'"></ngx-mat-timepicker>
          </mat-form-field>
        }
        @case (fieldTypes.FILE_UPLOAD) {
          <ee-uploader class="w-full" [form]="$any(form.get('value'))"
                       [ngClass]="{'hidden-from-intake': hideFromIntake}"></ee-uploader>
        }
        @case (fieldTypes.CALCULATED) {
          <ng-container [ngTemplateOutlet]="computedPreview"></ng-container>
        }
        @case (fieldTypes.DATE_OFFSET) {
          <ng-container [ngTemplateOutlet]="computedPreview"></ng-container>
        }
        @default {
            <mat-form-field class="flex-1" appearance="outline" [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
            <mat-label>{{form.get('label').value}}</mat-label>
            <!-- Gated on TEXT for the same reason as the intake renderer: several types fall
                 through to this branch, and a mask changes what is SAVED, not just what is shown. -->
            <input matInput [formControl]="valueControl" [required]="!builder && form.get('required').value"
                   [mask]="textMask" [attr.type]="inputType">
            @if (form.get('value')?.hasError('email')) {
              <mat-error>Enter a valid email address.</mat-error>
            } @else if (form.get('value')?.hasError('url')) {
              <mat-error>Enter a link starting with http:// or https://</mat-error>
            }
            <mat-hint>
              <ng-container  [ngTemplateOutlet]="hint"></ng-container>
            </mat-hint>
          </mat-form-field>
        }
      }

      <ng-template #computedPreview>
        <mat-form-field class="flex-1" appearance="outline"
                        [ngClass]="{'hidden-from-intake': hideFromIntake, 'compact': compact}">
          <mat-label>{{form.get('label').value}}</mat-label>
          <input matInput [placeholder]="computedPlaceholder" disabled readonly>
          <span matSuffix class="mr-2"><i [class]="computedIcon"></i></span>
          <mat-hint>Calculated automatically — the value appears on the case.</mat-hint>
        </mat-form-field>
      </ng-template>

      <ng-template #hint>
        <span>{{ form.get('required').value ? 'Required' : 'Optional' }}. </span>
        @if (form.get('hint')?.value) {
          <span>{{form.get('hint')?.value}}. </span>
        }
        @if (form.get('hint_more_info')?.value) {
          <span class="field-instructions-wrapper">
                  Hover for <span class="field-instructions" [tp]="form.get('hint_more_info').value" tpPlacement="bottom">
                  more info</span>.
          </span>
        }
      </ng-template>
    </div>
  `,
  styles: [`
    @use 'components/color-palette';

    /* A grid/flex item defaults to min-width:auto, i.e. it refuses to shrink below its content, which
       let a long checkbox label size the track past its container and scroll the form sideways.
       ee-field carries the same rule for the same reason — the two must not drift. */
    :host {
      min-width: 0;
    }

    .boolean-selection {
      min-width: 100px;
    }

    /* Kept in step with ee-field's .multi-select block: a fieldset will not shrink below its widest
       unbroken content (min-inline-size: min-content), and a mat-checkbox label takes its full
       one-line width, so a long court-document phrase pushes the group past its container unless
       both are bounded and the label is allowed to wrap. */
    .multi-select {
      display: flex;
      flex-direction: column;
      border: none;
      padding: 0;
      margin-inline: 0;
      margin-bottom: 16px;
      min-inline-size: 0;
      min-width: 0;
      max-width: 100%;

      .multi-select__label {
        font-size: 1rem;
        color: var(--color-on-surface);
        margin-bottom: 6px;
        padding: 0;
      }

      .multi-select__option {
        max-width: 100%;

        /* Centre the control against the label, as ee-field does (see field.component.ts). */
        ::ng-deep .mdc-form-field {
          max-width: 100%;
          align-items: center;
        }

        ::ng-deep .mdc-checkbox,
        ::ng-deep .mdc-radio {
          flex: 0 0 auto;
        }

        ::ng-deep label {
          min-width: 0;
          white-space: normal;
          overflow-wrap: anywhere;
          line-height: 1.3;
        }
      }

      .multi-select__error {
        margin-top: 4px;
      }

      .multi-select__required {
        color: var(--color-accent-danger-text);
      }

      .multi-select__hint {
        font-size: 75%;
        color: var(--color-on-surface-variant);
        margin-top: 4px;
      }
    }

    /* Stacked, like the intake renderer's radio group — a choice label is often a full sentence. Not the
       global "ee" radio-group class, which draws row-laid bordered cards meant for the Yes/No pills. */
    .choice-group {
      display: flex;
      flex-direction: column;
    }

    .choice-group.is-horizontal {
      flex-direction: row;
      flex-wrap: wrap;
      column-gap: 24px;
    }

    /* choice_layout HORIZONTAL: the checkboxes line up and wrap. They stay direct children of the
       fieldset, so only the fieldset's direction changes; the error and hint keep a line of their own. */
    .multi-select.is-horizontal {
      flex-direction: row;
      flex-wrap: wrap;
      column-gap: 24px;

      .multi-select__error,
      .multi-select__hint {
        flex-basis: 100%;
      }
    }

    .choice-group__none {
      color: rgba(0, 0, 0, 0.6);
      font-style: italic;
    }

    .choice-group__label {
      font-size: 0.75rem;
      color: rgba(0, 0, 0, 0.6);
      margin-bottom: 4px;
    }

    .field-instructions {
      color: var(--primary-red);
      text-decoration: underline;
      text-decoration-style: dashed;
      cursor: pointer;
      font-weight: 600;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomFieldComponent implements OnInit, OnDestroy {
  fieldTypes = CustomFieldType;

  /**
   * Whether a numeric field shows the `$` prefix. `null`/unset resolves to true, so every field
   * authored before the "Display as currency" toggle existed keeps its `$` and only an explicit
   * `false` drops it — the same rule the intake renderer applies.
   */
  get isMonetary(): boolean {
    return this.form?.get('monetary')?.value ?? true;
  }

  /** The stored ngx-mask pattern, or '' for an unrestricted input. */
  get mask(): string {
    return this.form?.get('mask')?.value || '';
  }

  /**
   * The mask for the fall-through text input — empty for every type but TEXT.
   *
   * Masks are TEXT-only (Req 9.4) and this branch renders more than TEXT, so gating on the type
   * rather than on merely having a pattern keeps a stray mask from reshaping a non-text value.
   */
  /** A browser input type for a formatted TEXT field, so mobile keyboards and autofill match. */
  get inputType(): string {
    if (this.form?.get('type')?.value !== CustomFieldType.TEXT) return 'text';
    const format = this.form.get('format')?.value;
    return format === TextFormat.EMAIL ? 'email' : format === TextFormat.URL ? 'url' : 'text';
  }

  get textMask(): string {
    return this.form?.get('type')?.value === CustomFieldType.TEXT ? this.mask : '';
  }

  /** A choice field whose options line up and wrap instead of stacking (`choice_layout`). */
  get horizontal(): boolean {
    return this.form?.get('choice_layout')?.value === ChoiceLayout.HORIZONTAL;
  }

  /**
   * The choices for a RADIO field, captioned by their Display_Label.
   *
   * Falls back to `options` (label equals value) when the field carries no option definitions, which
   * is every choice field authored before the {value, label} model existed. `GenerateCustomFieldForm`
   * carries `option_definitions` onto this form precisely so this can read them — without that, a
   * radio here could only ever be captioned with its raw stored value.
   */
  get choiceOptions(): { value: string; label: string }[] {
    const defined = this.form?.get('option_definitions')?.value as { value?: string; label?: string }[] | undefined;
    const base = defined?.length
      ? defined.map(o => ({value: (o.value ?? '').trim() || (o.label ?? ''), label: o.label ?? o.value ?? ''}))
      : ((this.form?.get('options')?.value as string[] | undefined) ?? []).map(value => ({value, label: value}));
    // Carry an ORPHANED stored value, exactly as the intake renderer's rebuildChoiceOptions does
    // (Req 5.5): if the option was renamed or deleted after this record was filled in, rendering only
    // the current options paints "nothing selected" while the control still holds — and re-saves —
    // the old value, so the answer silently misreports itself and is impossible to restore.
    const stored = this.valueControl?.value;
    if (typeof stored === 'string' && stored && !base.some(o => o.value === stored)) {
      return [...base, {value: stored, label: stored}];
    }
    return base;
  }
  @Input() form: UntypedFormGroup;
  @Input() builder = false;
  @Input() hideFromIntake = false;
  @Input() compact = true;

  /**
   * The MULTI_SELECT checkboxes to render: the field's own options followed by any stored value that
   * matches none of them (Req 5.5). Held as a field rather than a getter so it is not rebuilt on
   * every change-detection tick, and so unticking an orphan leaves its box on screen.
   */
  multiSelectOptions: CustomFieldOption[] = [];

  /** The field key {@link multiSelectOptions} was built for, so a rebuild knows whether the carried
   *  orphans still belong to the field being rendered. */
  private multiSelectOptionsKey: string | null = null;

  /** Serialized defined options, so an edit to the option rows (the builder preview edits them live)
   *  rebuilds the list while an ordinary value change does not. */
  private multiSelectDefinedSignature: string | null = null;

  private subs: Subscription[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  /**
   * The builder preview's own value, detached from the form.
   *
   * In the intake form builder `form` is the field DEFINITION (a placement), saved as a
   * `CustomFieldDTO` whose `value` is a `String?`. A preview that wrote into it would persist sample
   * input into the definition and mark the builder dirty — and a ticked checkbox group writes a
   * string[], which the server refuses to deserialize, failing the whole intake-form save. So the
   * preview stays interactive (an admin can see how the field behaves) but nothing typed or ticked
   * in it ever reaches the definition.
   */
  private readonly previewValue = new FormControl<any>(null);

  /** The type {@link previewValue} was last used for, so a type change starts the preview blank. */
  private previewType: CustomFieldType | null = null;

  ngOnInit() {
    this.previewType = this.form?.get('type')?.value ?? null;
    this.syncMultiSelectOptions();
    this.subs.push(this.form.valueChanges.subscribe( () => {
      const type = this.form.get('type')?.value ?? null;
      if (type !== this.previewType) {
        // A sample typed for one type means nothing to the next (a date string in a checkbox group).
        this.previewType = type;
        this.previewValue.reset(null);
      }
      this.syncMultiSelectOptions();
      this.cdr.detectChanges();
    }));
    this.subs.push(this.form.statusChanges.subscribe( () => {
      this.cdr.detectChanges();
    }));
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  /**
   * The control every input binds to: the form's `value` for a real record, the detached
   * {@link previewValue} in the builder. An array for MULTI_SELECT, a scalar for every other type.
   */
  get valueControl(): FormControl | null {
    if (this.builder) {
      return this.previewValue;
    }
    return (this.form?.get('value') as FormControl) ?? null;
  }

  /** The control a MULTI_SELECT group reads and writes — see {@link valueControl}. */
  get multiSelectControl(): AbstractControl | null {
    return this.valueControl;
  }

  /**
   * Id of the group's error message, so the fieldset can point at it with aria-describedby.
   *
   * Keyed on the field code, plus `order` as a discriminator: a not-yet-saved field has no code, so
   * two of them on one screen (the intake form builder renders a list) would otherwise share the id
   * `…-field` and each fieldset's aria-describedby would resolve to whichever element won.
   */
  get multiSelectErrorId(): string {
    const key = this.form?.get('field')?.value ?? `new-${this.form?.get('order')?.value ?? 0}`;
    return 'ee-custom-field-multi-select-error-' + key;
  }

  /**
   * Whether to show the "pick one" message. A required group is invalid while empty (Req 5.3), but
   * only says so once the user has been near it — otherwise every unfilled form opens shouting.
   */
  get showMultiSelectRequiredError(): boolean {
    const control = this.multiSelectControl;
    return !!control?.hasError('required') && (control.touched || control.dirty);
  }

  /** The control's MULTI_SELECT value — always an array, even when nothing has been stored yet. */
  private get selectedMultiSelectValues(): string[] {
    const value = this.multiSelectControl?.value;
    return Array.isArray(value) ? value : [];
  }

  isMultiSelectOptionSelected(value: string): boolean {
    return this.selectedMultiSelectValues.includes(value);
  }

  /**
   * Ticks/unticks one option. The control is rewritten in the rendered (authored, then orphaned)
   * order so the stored array does not depend on the order the boxes were clicked (Req 5.2), and
   * clearing every box writes an empty array — never null and never '' (Req 1.3).
   */
  toggleMultiSelectOption(value: string, checked: boolean): void {
    const control = this.multiSelectControl;
    if (!control || control.disabled) {
      return;
    }
    const selected = new Set(this.selectedMultiSelectValues);
    if (checked) {
      selected.add(value);
    } else {
      selected.delete(value);
    }
    control.setValue(this.multiSelectOptions.filter(o => selected.has(o.value)).map(o => o.value));
    control.markAsDirty();
    control.markAsTouched();
  }

  /**
   * Keeps {@link multiSelectOptions} in step with the form: rebuilt when the option rows themselves
   * change (the intake builder edits them live behind this preview), otherwise only extended with
   * stored values nothing renders yet.
   */
  private syncMultiSelectOptions(): void {
    if (this.form?.get('type')?.value !== CustomFieldType.MULTI_SELECT) {
      this.multiSelectOptions = [];
      this.multiSelectOptionsKey = null;
      this.multiSelectDefinedSignature = null;
      return;
    }
    const defined = this.definedMultiSelectOptions();
    const signature = JSON.stringify(defined);
    const key = this.form.get('field')?.value ?? null;
    if (signature !== this.multiSelectDefinedSignature || key !== this.multiSelectOptionsKey) {
      // Orphans already on screen are carried while it is still the same field — an orphan the user
      // has just UNTICKED is no longer in the value for the merge below to re-find, and dropping its
      // box would strand a value they can no longer restore (Req 5.5).
      // Only orphans — stored values the field does not define — are carried; a choice the field has
      // stopped offering was never a value, so it goes (as in ee-field).
      const carried = key === this.multiSelectOptionsKey
        ? this.multiSelectOptions.filter(o => this.multiSelectOrphans.has(o.value) && !defined.some(d => d.value === o.value))
        : [];
      this.multiSelectOptions = [...defined, ...carried];
      this.multiSelectOrphans = new Set(carried.map(o => o.value));
      this.multiSelectOptionsKey = key;
      this.multiSelectDefinedSignature = signature;
    }
    this.mergeStoredValuesIntoMultiSelectOptions();
    // This is the one branch that renders with [checked]/(change) rather than formControlName, so it
    // gets no ControlValueAccessor view sync. An external setValue/patchValue/reset must still
    // repaint an OnPush view.
    this.cdr.markForCheck();
  }

  /**
   * The authored options: the definition rows, or — for a field carrying only bare {@code options} —
   * one option per entry whose label equals its value (Req 2.2). A blank stored value means "defaults
   * to the label" (Req 4.6), resolved here so two such rows cannot collide on the track expression.
   */
  private definedMultiSelectOptions(): CustomFieldOption[] {
    // Shared with ee-field and the bulk-update dialog — see resolveMultiSelectOptions. Only the
    // carrier differs here: this renderer reads the definitions off the form, not off a field object.
    return resolveMultiSelectOptions({
      option_definitions: this.form.get('option_definitions')?.value as CustomFieldOption[] | undefined,
      options: this.form.get('options')?.value as string[] | undefined
    });
  }

  /**
   * Appends any stored value not already rendered. Only ever adds — unticking an orphaned value must
   * leave its checkbox on screen so it can be ticked back on.
   */
  /** The rendered choices that are stored values the field does not define (see the rebuild above). */
  private multiSelectOrphans = new Set<string>();

  private mergeStoredValuesIntoMultiSelectOptions(): void {
    const rendered = new Set(this.multiSelectOptions.map(o => o.value));
    const missing = [...new Set(this.selectedMultiSelectValues.filter(v => !rendered.has(v)))];
    if (missing.length) {
      this.multiSelectOptions = [...this.multiSelectOptions, ...missing.map(value => ({value, label: value}))];
      missing.forEach(value => this.multiSelectOrphans.add(value));
    }
  }

  /** Placeholder shown in the builder preview for computed (smart) fields, which have no entered value. */
  get computedPlaceholder(): string {
    return this.form.get('type')?.value === CustomFieldType.DATE_OFFSET
      ? 'Calculated date'
      : 'Calculated value';
  }

  get computedIcon(): string {
    return this.form.get('type')?.value === CustomFieldType.DATE_OFFSET
      ? 'far fa-calendar-days'
      : 'far fa-calculator';
  }
}
